/**
 * WHO OWNS a thing - one of the four independent relationships the architecture
 * refuses to collapse (ADR-63 rule 1 and rule 50): ownership, assignment,
 * custody and kit membership are separate, and none is inferred from another.
 *
 * This module names ownership only. Assignment relationships (RENTAL, LOANER,
 * LEASE_TO_OWN, ...) are the tenant's RIGHT TO USE a thing HipNumber may still
 * own, and they arrive with the assignment tables in a later cut. `TENANT_OWNED`
 * is deliberately NOT an owner type here: it is an assignment relationship for
 * human readability, and the doc is explicit that it must not be the source of
 * truth for ownership. The source of truth is `ownerType` + `ownerTenantId`.
 */

export const OWNER_TYPES = ['HIPNUMBER', 'TENANT', 'THIRD_PARTY', 'FINANCING_ENTITY', 'OTHER'] as const;
export type OwnerType = (typeof OWNER_TYPES)[number];

export const OWNER_TYPE_LABEL: Readonly<Record<OwnerType, string>> = {
  HIPNUMBER: 'HipNumber',
  TENANT: 'Tenant',
  THIRD_PARTY: 'Third party',
  FINANCING_ENTITY: 'Financing entity',
  OTHER: 'Other',
};

/**
 * Owner types that name a specific outside party. When the owner is one of
 * these, the record must identify WHICH one (a tenant id, or a free-text
 * external name) - the schema enforces the tenant case with a check constraint,
 * and this predicate is the same rule for the API and the form.
 */
export function ownerRequiresTenant(owner: OwnerType): boolean {
  return owner === 'TENANT';
}

export function isOwnerType(v: unknown): v is OwnerType {
  return typeof v === 'string' && (OWNER_TYPES as readonly string[]).includes(v);
}
